import Vue from "vue"
import { cloneDeep, get, isNil } from "lodash"

import {
  getSessionRequest,
  getSessionReportCard,
  markReportCardRecommendationCompletion,
  getSessionHistory,
} from "@/api/user/coaching/student/sessionRequests"
import { SESSION_REQUEST_STATUSES } from "@/constants/coaching"

const state = () => ({
  sessionRequestsMap: {},
  sessionRequestReportCardsMap: {},
  sessionHistory: null,
})

const mutations = {
  "SAVE_SESSION_REQUEST": (state, sessionRequest) => {
    Vue.set(state.sessionRequestsMap, sessionRequest.id, sessionRequest)
  },
  "SAVE_SESSION_REQUEST_REPORT_CARD": (state, { sessionRequestId, reportCard }) => {
    Vue.set(state.sessionRequestReportCardsMap, sessionRequestId, reportCard)
  },
  "setSessionHistory": (state, sessionHistory) => { state.sessionHistory = sessionHistory },
}

const actions = {
  async loadSessionRequest ({ state, commit, rootGetters }, sessionRequestId) {
    if (sessionRequestId in state.sessionRequestsMap) {
      return
    }

    const response = await getSessionRequest(rootGetters["User/Coaching/getInstaProAccessToken"], sessionRequestId)
    const sessionRequest = response.result
    commit("SAVE_SESSION_REQUEST", sessionRequest)
  },
  async loadSessionRequestReportCard ({ state, commit, dispatch, rootGetters }, sessionRequestId) {
    if (sessionRequestId in state.sessionRequestReportCardsMap) {
      return
    }

    try {
      const response = await getSessionReportCard(rootGetters["User/Coaching/getInstaProAccessToken"], sessionRequestId)
      const reportCard = response.result
      commit("SAVE_SESSION_REQUEST_REPORT_CARD", {
        sessionRequestId,
        reportCard,
      })
    } catch (e) {
      // If unable to resolve report card, check session request details to see
      // if we are expecting to be able to resolve one in the near future
      try {
        await dispatch("loadSessionRequest", sessionRequestId)
        const sessionRequest = state.sessionRequestsMap[sessionRequestId]
        const reportCardPending = get(sessionRequest, "report_card_required", false)
        if (!reportCardPending) {
          commit("SAVE_SESSION_REQUEST_REPORT_CARD", {
            sessionRequestId,
            reportCard: null,
          })
        }
      } catch (e) {
        console.error("Failed to resolve session request for report card in store.")
      }
    }
  },
  async flagReportCardRecommendationCompletion ({ state, commit, rootGetters }, { reportCardId, recommendationId, completed }) {
    await markReportCardRecommendationCompletion(rootGetters["User/Coaching/getInstaProAccessToken"], recommendationId, completed)

    // Update recommendation completion flags in store data
    let reportCard = cloneDeep(Object.values(state.sessionRequestReportCardsMap).find(reportCard => reportCard.id === reportCardId))
    if (reportCard) {
      const sessionRequestId = reportCard.request_id
      const recommendationIndex = reportCard.recommendations.findIndex(recommendation => recommendation.id === recommendationId)
      if (recommendationIndex !== -1) {
        reportCard.recommendations[recommendationIndex].completed_by_student = completed
      }

      commit("SAVE_SESSION_REQUEST_REPORT_CARD", {
        sessionRequestId,
        reportCard,
      })
    }
  },
  async loadSessionHistory ({ state, commit }, forceReload = false) {
    if (!forceReload && state.sessionHistory !== null) {
      return
    }

    const data = await getSessionHistory()
    await commit("setSessionHistory", data.data)
  },
}

const getters = {
  getSessionRequest: state => requestId => state.sessionRequestsMap[requestId],
  getSessionRequestReportCard: state => requestId => state.sessionRequestReportCardsMap[requestId],
  sessionHistory: state => {
    // Include all async requests and live session requests that were accepted
    return state.sessionHistory !== null
      ? state.sessionHistory
        .filter(session => {
          const isAsyncRequest = !isNil(session.async_param)
          const sessionStatus = session.status
          return isAsyncRequest ||
            ![SESSION_REQUEST_STATUSES.REJECTED, SESSION_REQUEST_STATUSES.CLOSED].includes(sessionStatus)
        })
      : null
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/user/coaching/student/sessionRequests.js